import { useRef, useState } from 'react';

export default function FinalCTA() {
  const formRef = useRef<HTMLFormElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;
    setSubmitting(true);
    const data = new URLSearchParams(new FormData(formRef.current) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d7fs3rcq2rhf4jj5qsqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-[#1a1a1a] py-24 px-6 md:px-16 lg:px-24">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* Left */}
        <div>
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-8 h-px bg-[#C9A84C]"></div>
            <span className="font-inter text-[#C9A84C] text-xs tracking-widest uppercase">Get Started</span>
          </div>
          <h2 className="font-barlow-condensed font-900 text-white text-4xl md:text-5xl lg:text-6xl leading-none mb-6">
            READY FOR A<br /><span className="text-[#C9A84C]">SPOTLESS FINISH?</span>
          </h2>
          <p className="font-inter text-white/60 text-sm leading-relaxed mb-8 max-w-md">
            Get professional construction cleaning that delivers a clean, polished result you can confidently hand off. Serving contractors and builders across Las Vegas.
          </p>
          <a
            href="tel:+17022494799"
            className="inline-flex items-center gap-3 border border-[#C9A84C]/40 hover:border-[#C9A84C] text-white font-barlow font-700 text-sm px-8 py-4 rounded-sm transition-all duration-200 cursor-pointer whitespace-nowrap tracking-widest uppercase"
          >
            <i className="ri-phone-line text-[#C9A84C]"></i>
            Call Now
          </a>
          <div className="mt-12 rounded-sm overflow-hidden h-56">
            <img
              src="https://public.readdy.ai/ai/img_res/b48dba6f9ce9c2571e7f29cae377e27c.jpg"
              alt="Spotless construction cleaning result Las Vegas"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        {/* Right - Form */}
        <div className="bg-white/5 border border-white/10 rounded-sm p-8 md:p-10">
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-[#C9A84C] rounded-sm mb-5">
                <i className="ri-check-line text-[#1a1a1a] text-2xl"></i>
              </div>
              <h3 className="font-barlow font-800 text-white text-xl mb-3">Quote Request Sent!</h3>
              <p className="font-inter text-white/60 text-sm leading-relaxed">We&apos;ll review your project details and get back to you shortly.</p>
            </div>
          ) : (
            <>
              <h3 className="font-barlow font-800 text-white text-lg tracking-wide mb-6">Request Your Quote Today</h3>
              <form ref={formRef} data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div>
                  <label className="font-inter text-white/50 text-xs tracking-wide uppercase block mb-1.5">Full Name *</label>
                  <input
                    name="name"
                    type="text"
                    required
                    placeholder="Your name"
                    className="w-full bg-white/8 border border-white/15 rounded-sm px-4 py-3 font-inter text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#C9A84C]/60 transition-colors"
                  />
                </div>
                <div>
                  <label className="font-inter text-white/50 text-xs tracking-wide uppercase block mb-1.5">Phone Number *</label>
                  <input
                    name="phone"
                    type="tel"
                    required
                    placeholder="(702) 000-0000"
                    className="w-full bg-white/8 border border-white/15 rounded-sm px-4 py-3 font-inter text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#C9A84C]/60 transition-colors"
                  />
                </div>
                <div>
                  <label className="font-inter text-white/50 text-xs tracking-wide uppercase block mb-1.5">Email</label>
                  <input
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    className="w-full bg-white/8 border border-white/15 rounded-sm px-4 py-3 font-inter text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#C9A84C]/60 transition-colors"
                  />
                </div>
                <div>
                  <label className="font-inter text-white/50 text-xs tracking-wide uppercase block mb-1.5">Project Type *</label>
                  <select
                    name="project_type"
                    required
                    className="w-full bg-[#2a2a2a] border border-white/15 rounded-sm px-4 py-3 font-inter text-white text-sm focus:outline-none focus:border-[#C9A84C]/60 transition-colors cursor-pointer"
                  >
                    <option value="">Select project type</option>
                    <option value="Post-Construction Cleaning">Post-Construction Cleaning</option>
                    <option value="Final Cleaning">Final Cleaning</option>
                    <option value="Rough Cleaning">Rough Cleaning</option>
                    <option value="Commercial Cleaning">Commercial Cleaning</option>
                    <option value="Debris & Dust Removal">Debris &amp; Dust Removal</option>
                  </select>
                </div>
                <div>
                  <label className="font-inter text-white/50 text-xs tracking-wide uppercase block mb-1.5">Message</label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Tell us about your project — size, location, timeline..."
                    className="w-full bg-white/8 border border-white/15 rounded-sm px-4 py-3 font-inter text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#C9A84C]/60 transition-colors resize-none"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#C9A84C] hover:bg-[#E8C96A] disabled:opacity-60 text-[#1a1a1a] font-barlow font-800 text-sm py-4 rounded-sm transition-all duration-200 cursor-pointer whitespace-nowrap tracking-widest uppercase mt-2"
                >
                  {submitting ? 'Sending...' : 'Request Your Quote Today'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
